import { DRIVER_STATUS } from '@allocab/allocab-commons-shared-types';
import { DriverWithStatusVehicleTripCountOutput } from '@allocab/allocab-driver-service-types';
import { EyeSolid, MagnifyingGlassSolid } from '@allocab/allocab-icons';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Link } from '../../components/atomic/Link';
import { TextInput } from '../../components/atomic/TextInput';
import { BadgeIndicator } from '../../components/BadgeIndicator';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { LicensePlate } from '../../components/LicensePlate';
import { Table } from '../../components/table/Table';
import { TableColumn } from '../../components/table/types';
import useDebounce from '../../hooks/useDebounce';
import { DRIVERS_LIMIT, prefetchDriversPage, useFethDriversPage } from '../../hooks/useFetchDrivers';
import { t } from '../../services/translation/translation';
import { getFullName } from '../../utils/common';
import { formatDate } from '../../utils/date';
import { getStatusText } from '../../utils/driver';
import { getVehicleName } from '../../utils/vehicle';

const columns: TableColumn<DriverWithStatusVehicleTripCountOutput>[] = [
  {
    name: t('common.name'),
    render: driver => <Link to={`/drivers/${driver.id}`}>{getFullName(driver.firstName, driver.lastName)}</Link>,
    sortKey: 'firstName',
  },
  {
    name: t('common.status'),
    render: driver => {
      const status = driver.status as DRIVER_STATUS;

      return (
        <div className="w-44">
          <BadgeIndicator
            variant={
              status === 'ACTIVATED'
                ? 'green'
                : status === 'DEACTIVATED' || status === 'SUSPENDED'
                ? 'red'
                : status === 'SUBSCRIPTION' || status === 'SUBSCRIPTION_REVIEW'
                ? 'yellow'
                : 'gray'
            }
          >
            {getStatusText(status)}
          </BadgeIndicator>
        </div>
      );
    },
    sortKey: 'status',
  },
  {
    name: t('common.see-price'),
    render: driver => {
      return (
        <div className="min-w-[120px] flex justify-center">
          {driver.params.canSeePrice ? (
            <EyeSolid width={16} height={16} className="fill-green-500" />
          ) : (
            <EyeSolid width={16} height={16} className="fill-red-500" />
          )}
        </div>
      );
    },
    sortKey: 'canSeePrice',
  },
  { name: t('common.phone'), render: driver => driver.phoneNumber, sortKey: 'phoneNumber' },
  { name: t('common.email'), render: driver => driver.email, sortKey: 'email' },
  {
    name: t('drivers-route.active-from'),
    render: driver => (driver.statusUpdatedAt ? formatDate(driver.statusUpdatedAt, t('date.datetime')) : '--'),
    sortKey: 'statusUpdatedAt',
  },
  { name: t('drivers-route.nb-trips'), render: driver => driver.tripCount, sortKey: 'tripCount' },
  {
    name: t('drivers-route.affected-vehicle'),
    render: driver =>
      driver.vehicle ? (
        <Link to={`/vehicles/${driver.vehicle.id}`} className="flex-col inline-flex items-start gap-2 min-w-[144px]">
          <LicensePlate plateNumber={driver.vehicle.licensePlate} />
          <span className="capitalize">{getVehicleName(driver.vehicle).toLocaleLowerCase()}</span>
        </Link>
      ) : (
        '--'
      ),
  },
];

export const DriversTable = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const currentPage = Math.max(parseInt(searchParams.get('page') ?? '1'), 1);
  const orderBy = searchParams.get('orderBy') ?? undefined;
  const direction = (searchParams.get('direction') ?? undefined) as 'desc' | 'asc' | undefined;
  const { data, isLoading, isFetching, isPreviousData } = useFethDriversPage(
    { search: debouncedSearchQuery, direction, orderBy },
    currentPage,
  );
  const nbPages = Math.ceil((data?.totalItems ?? 0) / DRIVERS_LIMIT);

  useEffect(() => {
    if (nbPages) {
      if (currentPage > 1) {
        prefetchDriversPage(undefined, currentPage - 1);
      }

      if (currentPage < nbPages) {
        prefetchDriversPage(undefined, currentPage + 1);
      }
    }
  }, [currentPage, nbPages]);

  return (
    <div className="space-y-2">
      <TextInput
        placeholder={t('invoicing-route.search-driver')}
        className="min-w-full xl:w-56 xl:min-w-0"
        value={searchQuery}
        onChange={e => setSearchQuery(e.currentTarget.value)}
        icon={<MagnifyingGlassSolid width={16} height={16} className="fill-current" />}
      />
      <Table
        identifier="id"
        columns={columns}
        data={data?.items}
        isLoading={isLoading}
        isFetching={isPreviousData && isFetching}
        nbPages={nbPages}
        context={undefined}
        totalItems={data?.totalItems ?? 0}
        title={t('drivers-route.table-title')}
        caption={t('drivers-route.caption')}
        action={<PrimaryButton text={t('common.add')} onClick={() => navigate('add')} />}
      />
    </div>
  );
};
